import { useMemo } from 'react'
import { useReadBoards } from '@opallabs/api'
import { findResourceOfType, OpalApiResponseDocument } from '@opallabs/api/v3'
import { useWorkspace } from '@opallabs/hooks'
import { useMomentData } from '../AddMomentToBoardModal/queries'

export type MomentBoard = Readonly<{
  id: string
  title: string
  isPublic: boolean
  ownerName?: string
  ownerAvatarUrl?: string
}>

type BoardsResponseDocument = OpalApiResponseDocument<'ReadBoardsV3', 200>

export const selectMomentBoards = (
  document: BoardsResponseDocument
): MomentBoard[] =>
  document.data.map((board) => {
    const owner = document.included?.find(
      findResourceOfType(
        'user',
        'V3',
        board.relationships.created_by_user.data.id
      )
    )
    const profile = owner
      ? document.included?.find(
          findResourceOfType(
            'user_profile',
            'V3',
            owner.relationships.profile.data?.id
          )
        )
      : undefined
    const avatar = profile
      ? document.included?.find(
          findResourceOfType(
            'asset_reference',
            'V3',
            profile.relationships.avatar_asset_reference.data?.id
          )
        )
      : undefined

    return {
      id: board.id,
      title: board.attributes.title,
      isPublic: board.attributes.is_public,
      ownerName: owner?.attributes.full_name,
      ownerAvatarUrl: avatar?.attributes.full_url ?? undefined,
    }
  })

/**
 * The boards a moment belongs to, with each board's owner and privacy state.
 * The board_objects include on moments cannot nest board details, so this is
 * two requests: the moment's board ids, then those boards with their owners.
 *
 * The caller mounts and unmounts this with the popover, so re-opening remounts
 * it while both queries revalidate in the background. Cached boards are not
 * `isLoading`, or every re-open would replace the rows with a spinner.
 */
export const useMomentBoards = (momentUuid: string) => {
  const { uuid: workspaceUuid } = useWorkspace()

  const momentsResult = useMomentData({ momentUuids: [momentUuid] })
  const boardIds = useMemo(
    () => Object.keys(momentsResult.data?.[0]?.boardObjectsByBoard ?? {}),
    [momentsResult.data]
  )

  const boardsResult = useReadBoards({
    enabled: boardIds.length > 0,
    workspaceUuid,
    query: {
      filter: { id: boardIds },
      fields: { board: 'title,is_public,created_by_user' },
      include: [
        'created_by_user',
        'created_by_user.profile',
        'created_by_user.profile.avatar_asset_reference',
      ],
    },
    select: selectMomentBoards,
  })

  const boards = boardIds.length === 0 ? [] : (boardsResult.data ?? [])

  return {
    boards,
    isLoading:
      boards.length === 0 &&
      (!momentsResult.fetchingComplete ||
        (boardIds.length > 0 && boardsResult.isLoading)),
  }
}
